import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { Comment, Review } from "../interfaces/types";
import { addOrUpdateCommentInFirebase, fetchAllComments } from "../services/firebaseService";
import { useUserContext } from "./userProvider";

export type CommentContextType = {
  comments: Comment[];
  addComment: (newComment: Comment) => void;
  updateComment: (updatedComment: Comment) => void;
  getCommentsForReview: (review: Review) => Comment[];
};

const CommentContext = createContext<CommentContextType>({
  comments: [],
  addComment: () => { },
  updateComment: () => { },
  getCommentsForReview: () => [],
});

export const CommentProvider: FC<{ children: ReactNode; }> = ({ children }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const { user, userDetails } = useUserContext();

  useEffect(() => {
    refreshComments();
  }, [user]);

  function refreshComments(){
    if (user) {
      fetchAllComments().then(fetchedComments => {
        fetchedComments = fetchedComments?.filter(comment => comment.id);
        setComments(fetchedComments as Comment[]);
      });
    }
  }

  function addComment(newComment: Comment) {
    addOrUpdateCommentInFirebase(newComment);
    setComments([...comments, newComment]);
  }

  function updateComment(updatedComment: Comment) {
    addOrUpdateCommentInFirebase(updatedComment);

    const updatedComments = comments?.map(comment => {
      if (comment.id === updatedComment.id) {
        return updatedComment;
      }
      return comment;
    });
    setComments(updatedComments);
  }

  function getCommentsForReview(review: Review) {
    return comments.filter(comment => comment.reviewId === review.id);
  }

  const value: CommentContextType = {
    comments,
    addComment,
    updateComment,
    getCommentsForReview,
  };

  return <CommentContext.Provider value={value}>{children}</CommentContext.Provider>;
};

export const useCommentContext = (): CommentContextType => {
  return useContext(CommentContext);
};
